import React, { useEffect, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stage, useGLTF } from '@react-three/drei';
import * as THREE from 'three';

/**
 * Component to render a GLB/GLTF model
 */
function ModelViewer({ modelPath }) {
  const { scene } = useGLTF(modelPath);
  const groupRef = useRef();

  useEffect(() => {
    if (scene && groupRef.current) {
      // Clone the scene to avoid reuse issues
      scene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          // Ensure the mesh has proper materials
          if (!child.material) {
            child.material = new THREE.MeshStandardMaterial({
              color: 0x888888,
              metalness: 0.5,
              roughness: 0.5,
            });
          }
        }
      });
    }
  }, [scene]);

  return <primitive object={scene} ref={groupRef} />;
}

/**
 * 3D Canvas Viewer with lighting and controls
 */
export default function ModelCanvas({ modelPath }) {
  if (!modelPath) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1a1a1a',
          color: '#aaa',
          fontSize: '18px',
        }}
      >
        <p>Upload a 3D model to view it here</p>
      </div>
    );
  }

  return (
    <Canvas
      camera={{ position: [0, 0, 5], fov: 50 }}
      style={{ width: '100%', height: '100%' }}
      gl={{ antialias: true, pixelRatio: window.devicePixelRatio }}
    >
      {/* Lighting setup */}
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 10, 10]} intensity={1} />
      <directionalLight position={[-10, -10, 5]} intensity={0.3} />

      {/* Model viewer wrapped in Stage for auto-scaling */}
      <Stage environment="studio" shadows adjustCamera scale={1.5}>
        <ModelViewer modelPath={modelPath} />
      </Stage>

      {/* Orbit controls */}
      <OrbitControls
        autoRotate
        autoRotateSpeed={3}
        dampingFactor={0.05}
        enableDamping
        maxDistance={20}
        minDistance={2}
      />
    </Canvas>
  );
}
